/**
 * Upload API Service
 * Handles all file upload batch related API calls
 * Uses the existing ApiService for authentication
 */
import apiService from './api';

// Types
export interface BatchCreateRequest {
  client_name: string;
  total_files: number;
}

export interface BatchCreateResponse {
  batch_id: string;
  upload_url?: string;
  expires_in: number;
  message: string;
}

export interface FileUploadResponse {
  filename: string;
  status: string;
  file_id: string;
  size_bytes?: number;
  message?: string;
}

export interface BatchUploadResponse {
  batch_id: string;
  uploaded: number;
  failed: number;
  files: FileUploadResponse[];
}

export interface FileStatusDetail {
  id: string;
  filename: string;
  file_size_bytes: number;
  file_type: string;
  status: string;
  status_details?: string;
  pages_extracted?: number;
  articles_extracted?: number;
  processing_time_seconds?: number;
  error_message?: string;
  uploaded_at: string;
  completed_at?: string;
  processing_started_at?: string;
}

export interface BatchStatusResponse {
  batch_id: string;
  uploaded_by_name: string;
  client_name: string;
  status: string;
  total_files: number;
  files_uploaded: number;
  files_processed: number;
  articles_extracted: number;
  progress_percentage: number;
  openai_file_id?: string;
  combined_csv_path?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  files: FileStatusDetail[];
}

export interface BatchListItem {
  batch_id: string;
  uploaded_by_name: string;
  client_name: string;
  status: string;
  total_files: number;
  files_processed: number;
  articles_extracted: number;
  progress_percentage: number;
  created_at: string;
  completed_at?: string;
}

export interface BatchListResponse {
  batches: BatchListItem[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface BatchListParams {
  page?: number;
  limit?: number;
  status_filter?: string;
  client?: string;
  period?: 'today' | 'week' | 'month' | 'all';
}

export interface ActivityFeedItem {
  id: string;
  action_type: string;
  user_name: string;
  batch_id?: string;
  message: string;
  created_at: string;
}

export interface ActivityFeedResponse {
  activities: ActivityFeedItem[];
  total: number;
}

export interface GlobalStatsResponse {
  batches_in_progress: number;
  batches_today: number;
  batches_this_month: number;
  total_files_processed_today: number;
  total_articles_extracted_today: number;
  recent_clients: string[];
}

/**
 * Upload API client
 */
class UploadApiService {
  /**
   * Create a new batch
   */
  async createBatch(data: BatchCreateRequest): Promise<BatchCreateResponse> {
    const response = await apiService.post<BatchCreateResponse>('/api/batches/create', data);
    return response.data;
  }

  /**
   * Upload files to a batch
   */
  async uploadFiles(batchId: string, files: File[]): Promise<BatchUploadResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Use custom headers for multipart/form-data
    const response = await apiService.post<BatchUploadResponse>(
      `/api/batches/${batchId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  /**
   * Get list of batches with pagination and filters
   */
  async getBatchList(params: BatchListParams = {}): Promise<BatchListResponse> {
    const response = await apiService.get<BatchListResponse>('/api/batches/list', { params });
    return response.data;
  }

  /**
   * Get detailed status of a specific batch
   */
  async getBatchStatus(batchId: string): Promise<BatchStatusResponse> {
    const response = await apiService.get<BatchStatusResponse>(`/api/batches/${batchId}/status`);
    return response.data;
  }

  /**
   * Get activity feed (recent activities from all users)
   */
  async getActivityFeed(limit: number = 100): Promise<ActivityFeedResponse> {
    const response = await apiService.get<ActivityFeedResponse>('/api/batches/activity/feed', {
      params: { limit },
    });
    return response.data;
  }

  /**
   * Get global statistics
   */
  async getGlobalStats(): Promise<GlobalStatsResponse> {
    const response = await apiService.get<GlobalStatsResponse>('/api/batches/stats/global');
    return response.data;
  }

  /**
   * Download batch file (combined CSV or OpenAI file)
   */
  async downloadBatch(batchId: string): Promise<any> {
    const response = await apiService.get(`/api/batches/${batchId}/download`);
    return response.data;
  }

  /**
   * Delete a batch
   */
  async deleteBatch(batchId: string): Promise<void> {
    await apiService.delete(`/api/batches/${batchId}`);
  }

  /**
   * Re-upload a batch to OpenAI assistant (Admin only)
   */
  async reuploadBatchToAssistant(batchId: string): Promise<{ success: boolean; message: string; openai_file_id: string }> {
    const response = await apiService.post<{ success: boolean; message: string; openai_file_id: string }>(
      `/api/batches/${batchId}/reupload-to-assistant`
    );
    return response.data;
  }

  /**
   * Force finalization of a stuck batch (triggers aggregation)
   */
  async forceFinalizeBatch(batchId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiService.post<{ success: boolean; message: string }>(
      `/api/batches/${batchId}/force-finalize`
    );
    return response.data;
  }

  /**
   * Reprocess stuck files (republish to Pub/Sub)
   */
  async reprocessStuckFiles(batchId: string): Promise<{
    success: boolean;
    message: string;
    republished: { extract: number; enrich: number };
  }> {
    const response = await apiService.post<{
      success: boolean;
      message: string;
      republished: { extract: number; enrich: number };
    }>(`/api/batches/${batchId}/reprocess-stuck-files`);
    return response.data;
  }
}

// Export singleton instance
const uploadApiService = new UploadApiService();
export default uploadApiService;

